import React, { useState } from 'react';
import NavigationBar from '../navbar/NavigationBar';
import Footer from '../navbar/Footer';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Chip,
  Snackbar,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  alpha,
  useTheme
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import HomeIcon from '@mui/icons-material/Home';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import DescriptionIcon from '@mui/icons-material/Description';
import CampaignIcon from '@mui/icons-material/Campaign';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import BusinessIcon from '@mui/icons-material/Business';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

const DocumentsInfographies = () => {
  const [downloadLoading, setDownloadLoading] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [expandedDoc, setExpandedDoc] = useState(null);
  const theme = useTheme();

  const documents = [
    {
      id: 1,
      title: "COMMENT VA NOTRE ÉCONOMIE ?",
      subtitle: "Analyse Économique 2025",
      type: "depliant",
      category: "economie",
      summary: "État des lieux complet de l'économie burundaise avec indicateurs clés, tendances macroéconomiques et perspectives de croissance.",
      year: "2025",
      pdfUrl: "/documents/rapports/Depl_conomie_2025.pdf", // Chemin corrigé
      icon: <TrendingUpIcon />,
      features: ["Indicateurs macroéconomiques", "Analyse sectorielle", "Perspectives de croissance"],
      sections: [
        "NOTRE BUDGET EMPLOI & ACTIVITÉ",
        "INDICES DES PRIX À LA PRODUCTION AGRICOLE",
        "COMMERCE : UNE CROISSANCE SOLIDE", 
        "SERVICES : UN REPLI GÉNÉRAL",
        "PRODUCTION INDUSTRIELLE",
        "VUE D'ENSEMBLE ÉCONOMIQUE"
      ],
      event: "Mois de la Statistique 2025"
    },
    {
      id: 2,
      title: "Commerce & Construction",
      subtitle: "Deux moteurs de l'économie burundaise",
      type: "depliant",
      category: "sectoriel",
      summary: "Étude approfondie des secteurs du commerce et de la construction, analysant leur contribution à la croissance économique.",
      year: "2024",
      pdfUrl: "/documents/rapports/Depl_Commerce_Exterieur_2024_ICCLB_aout_2025.pdf", // Chemin corrigé
      icon: <BusinessIcon />,
      features: ["Commerce extérieur", "Investissements construction", "Analyse comparative"],
      sections: [
        "COMMERCE EXTÉRIEUR - Tendance Générale 2024",
        "EXPORTATIONS - Produits Exportés",
        "IMPORTATIONS - Produits importés", 
        "COMMERCE INFORMEL - Échanges transfrontaliers",
        "COÛT CONSTRUCTION - Indice ICCLB"
      ],
      event: "Mois de la Statistique 2025"
    }
  ];

  const stats = [
    { number: documents.length.toString(), label: "Documents Disponibles", icon: <DescriptionOutlinedIcon /> },
    { number: "2024-2025", label: "Données Actualisées", icon: <CalendarTodayIcon /> },
  ];

  const [filter, setFilter] = useState('all');

  const filteredDocuments = filter === 'all' 
    ? documents 
    : documents.filter(doc => doc.type === filter);

  const getTypeColor = (type) => {
    const colors = {
      'depliant': '#008D36',
      'infographie': '#008D36',
      'rapport':'#008D36',
      'brochure': '#008D36'
    };
    return colors[type] || '#757575';
  };

  const getTypeIcon = (type) => {
    const icons = {
      'depliant': <CampaignIcon />,
      'infographie': <ImageIcon />,
      'rapport': <DescriptionIcon />,
      'brochure': <PictureAsPdfIcon />
    };
    return icons[type] || <DescriptionIcon />;
  };

  const getCategoryColor = (category) => {
    const colors = {
      'economie': '#008D36',
      'sectoriel': '#008D36',
      'annuel': '#008D36',
      'institutionnel': '#008D36'
    };
    return colors[category] || '#757575';
  };

  const handleDownload = async (document) => {
    setDownloadLoading(document.id);
    
    try {
      // Vérifier si le fichier existe
      const response = await fetch(document.pdfUrl);
      
      if (!response.ok) {
        throw new Error('Fichier non trouvé');
      }

      // Créer un blob et télécharger
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Extraire le nom du fichier de l'URL
      const filename = document.pdfUrl.split('/').pop() || 'document.pdf';
      link.download = filename;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Nettoyer l'URL
      window.URL.revokeObjectURL(url);
      
      setSnackbar({
        open: true,
        message: `Téléchargement de "${document.title}" réussi`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Erreur de téléchargement:', error);
      
      // Solution alternative : ouvrir dans un nouvel onglet si le téléchargement direct échoue
      try {
        window.open(document.pdfUrl, '_blank');
        setSnackbar({
          open: true,
          message: `Ouverture de "${document.title}" dans un nouvel onglet`,
          severity: 'info'
        });
      } catch (fallbackError) {
        setSnackbar({
          open: true,
          message: 'Document non disponible pour le moment. Veuillez réessayer plus tard.',
          severity: 'error'
        });
      }
    } finally {
      setDownloadLoading(null);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getTypeDisplayName = (type) => {
    const names = {
      'depliant': 'Dépliant',
      'infographie': 'Infographie',
      'rapport': 'Rapport',
      'brochure': 'Brochure'
    };
    return names[type] || type;
  };

  const getCategoryDisplayName = (category) => {
    const names = {
      'economie': 'Économie',
      'sectoriel': 'Sectoriel',
      'annuel': 'Annuel',
      'institutionnel': 'Institutionnel'
    };
    return names[category] || category;
  };

  const toggleExpand = (docId) => {
    setExpandedDoc(expandedDoc === docId ? null : docId);
  };

  return (
    <div style={{ backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      <NavigationBar />
      
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Fil d'Ariane */}
        <Breadcrumbs sx={{ mb: 4, px: { xs: 2, sm: 0 } }}>
          <Link underline="hover" color="inherit" href="/" sx={{ display: 'flex', alignItems: 'center' }}>
            <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
            Accueil
          </Link>
          <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
            <ImageIcon sx={{ mr: 0.5 }} fontSize="small" />
            Documents & Infographies
          </Typography>
        </Breadcrumbs>

        {/* En-tête Hero */}
        <Box sx={{ 
          textAlign: 'center', 
          mb: 8,
          px: { xs: 2, sm: 0 }
        }}>
          <Typography 
            variant="h5" 
            component="h6" 
            gutterBottom 
            sx={{ 
              fontWeight: '600', 
              color: '#008D36',
              fontSize: { xs: '1.1rem', md: '1.5rem' },
              background: 'linear-gradient(135deg, #008D36 0%, #006627 100%)',
              backgroundClip: 'text',
              textFillColor: 'transparent',
              mb: 2
            }}
          >
            Documents & Infographies
          </Typography>
           <Typography variant="body1" align="center" gutterBottom>
            Découvrez nos analyses économiques et sectorielles à travers des documents 
            conçus pour une compréhension rapide et claire
          </Typography>
        </Box>

        {/* Statistiques */}
        <Grid container spacing={2} sx={{ mb: 6, px: { xs: 2, sm: 0 } }}>
          {stats.map((stat, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Box sx={{
                textAlign: 'center',
                p: 3,
                backgroundColor: 'white',
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                border: '1px solid',
                borderColor: 'divider',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 30px rgba(0,0,0,0.12)'
                }
              }}>
                <Box sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  backgroundColor: alpha('#008D36', 0.1),
                  color: '#008D36',
                  mb: 2
                }}>
                  {stat.icon}
                </Box>
                <Typography variant="h5" component="div" sx={{ 
                  fontWeight: '400', 
                  color: '#008D36',
                  mb: 1
                }}>
                  {stat.number}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: '500' }}>
                  {stat.label}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Filtres */}
        <Box sx={{ 
          mb: 6, 
          display: 'flex', 
          justifyContent: 'center', 
          flexWrap: 'wrap', 
          gap: 1,
          px: { xs: 2, sm: 0 }
        }}>
          {[
            { key: 'all', label: 'Tous les documents' },
            { key: 'depliant', label: 'Dépliants' },
            { key: 'rapport', label: 'Rapports' }
          ].map((item) => (
            <Chip
              key={item.key}
              label={item.label}
              onClick={() => setFilter(item.key)}
              variant={filter === item.key ? "filled" : "outlined"}
              sx={{ 
                fontWeight: '600',
                minWidth: 140,
                height: 40,
                backgroundColor: filter === item.key ? '#008D36' : 'transparent',
                color: filter === item.key ? 'white' : '#008D36',
                borderColor: '#008D36',
                '&:hover': {
                  backgroundColor: filter === item.key ? '#008D36' : alpha('#008D36', 0.04)
                },
                fontSize: '0.9rem'
              }}
            />
          ))}
        </Box>

        {/* Grille des documents */}
        <Grid container spacing={2} sx={{ px: { xs: 2, sm: 0 } }}>
          {filteredDocuments.map((doc) => (
            <Grid item xs={12} lg={6} key={doc.id}>
              <Card 
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.3s ease-in-out',
                  border: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: 'white',
                  borderRadius: 3,
                  overflow: 'visible',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  '&:hover': {
                    boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
                    borderColor: getTypeColor(doc.type),
                    transform: 'translateY(-8px)'
                  }
                }}
              >
                <CardContent sx={{ p: 4, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  {/* En-tête de la carte */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Chip
                        icon={getTypeIcon(doc.type)}
                        label={getTypeDisplayName(doc.type)}
                        size="medium"
                        sx={{
                          backgroundColor: getTypeColor(doc.type),
                          color: 'white',
                          fontWeight: '700',
                          fontSize: '0.8rem'
                        }}
                      />
                      <Chip
                        label={getCategoryDisplayName(doc.category)}
                        size="small"
                        variant="outlined"
                        sx={{
                          borderColor: getCategoryColor(doc.category),
                          color: getCategoryColor(doc.category),
                          fontWeight: '600'
                        }}
                      />
                      {doc.event && (
                        <Chip
                          label={doc.event}
                          size="small"
                          variant="outlined"
                          sx={{
                            borderColor: '#008D36',
                            color: '#008D36',
                            fontWeight: '600',
                            fontSize: '0.7rem'
                          }}
                        />
                      )}
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="body2" color="text.secondary" display="block" sx={{ fontWeight: '600' }}>
                        {doc.year}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Icône et Titre */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 50,
                      height: 50,
                      borderRadius: 2,
                      backgroundColor: alpha('#008D36', 0.1),
                      color: '#008D36',
                      flexShrink: 0
                    }}>
                      {doc.icon}
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          fontWeight: '700',
                          lineHeight: 1.3,
                          color: '#1a1a1a',
                          mb: 1
                        }}
                      >
                        {doc.title}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: '400',
                          color: '#008D36'
                        }}
                      >
                        {doc.subtitle}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Résumé */}
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ mb: 3, lineHeight: 1.6, flexGrow: 1 }}
                  >
                    {doc.summary}
                  </Typography>

                  {/* Caractéristiques */}
                
                  {/* Sections détaillées (expandable) */}
                 
                  {/* Métadonnées et Bouton */}
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    mt: 'auto',
                    pt: 2,
                    borderTop: '1px solid',
                    borderColor: 'divider'
                  }}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        🎯 {getCategoryDisplayName(doc.category)}
                      </Typography>
                    </Box>
                    
                    <Button
                      variant="contained"
                      startIcon={
                        downloadLoading === doc.id ? 
                        <CircularProgress size={18} /> : 
                        <DownloadIcon />
                      }
                      disabled={downloadLoading === doc.id}
                      onClick={() => handleDownload(doc)}
                      sx={{
                        px: 3,
                        py: 1.2,
                        backgroundColor: '#006627',
                        borderRadius: 2,
                        fontWeight: '700',
                        fontSize: '0.9rem',
                        textTransform: 'none',
                        minWidth: 180,
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          backgroundColor: '#006627',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 6px 20px rgba(0, 141, 54, 0.3)'
                        },
                        '&:active': {
                          transform: 'translateY(0)'
                        }
                      }}
                    >
                      {downloadLoading === doc.id ? 'Téléchargement...' : 'Télécharger le PDF'}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Section d'information */}
        <Box sx={{ 
          mt: 10, 
          p: 5, 
          backgroundColor: 'white', 
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          textAlign: 'center',
          border: '1px solid',
          borderColor: 'divider',
          mx: { xs: 2, sm: 0 }
        }}>
          <Box sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 80,
            height: 80,
            borderRadius: '50%',
            backgroundColor: alpha('#008D36', 0.1),
            color: '#008D36',
            mb: 3
          }}>
            <ImageIcon sx={{ fontSize: 40 }} />
          </Box>
          <Typography 
            variant="h6" 
            sx={{ 
              color: '#008D36',
              mb: 2,
              fontWeight: '400'
            }}
          >
            Documents Officiels de l'INS Burundi
          </Typography>
          <Typography variant="body1" align="center" gutterBottom>
            Nos documents sont conçus pour offrir une vision claire et accessible des données économiques 
            et sectorielles du Burundi. Chaque publication respecte les standards internationaux 
            de qualité statistique.
          </Typography>
        </Box>
      </Container>

      <Footer />

      {/* Notification */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={4000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          severity={snackbar.severity} 
          sx={{ 
            width: '100%',
            backgroundColor: snackbar.severity === 'success' ? '#008D36' : '#E30613',
            color: 'white',
            fontWeight: '600',
            borderRadius: 2,
            '& .MuiAlert-icon': {
              color: 'white'
            }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default DocumentsInfographies;